import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { debounceTime, delay, Subject, Subscription } from 'rxjs';
import { safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import ProjectLinks from 'src/app/common/project.links';
import { DialogMessagePlaceholders } from 'src/app/notifications/dialogs/dialog-message/dialog-message-placeholders.model';
import { ChatEngineActiveChannelService } from '../interfaces/chat-engine/chat-engine-active-channel.service';
import { ChatConversation, ChatMessage } from '../interfaces/chat-model.interface';
import {
  ChatPermissions,
  ChatTitleSectionConfiguration,
  EditMessageBody,
} from '../interfaces/chat-operations.interface';

@Component({
  selector: 'app-chat-content',
  templateUrl: './chat-content.component.html',
  styleUrls: ['./chat-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatContentComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true }) public messages: ChatMessage[];
  @Input({ required: true }) public currentUserId: string;
  @Input({ required: true }) public isLoadingMessages: boolean;
  @Input({ required: true }) public memberTypingName: string | null;
  @Input({ required: true }) public channelExists: boolean;
  @Input({ required: true }) public activeConversation: ChatConversation | null;
  @Input({ required: true }) public permissions: ChatPermissions;
  @Input({ required: true }) public titleConfiguration: ChatTitleSectionConfiguration | null;
  @Input({ required: true }) public accountStatusHint: string;
  @Input({ required: true }) public displayAccountStatusHint;

  @Output() public sendTextMessage: EventEmitter<string> = new EventEmitter<string>();
  @Output() public messageEdited: EventEmitter<EditMessageBody> = new EventEmitter<EditMessageBody>();
  @Output() public messageRemoved: EventEmitter<ChatMessage> = new EventEmitter<ChatMessage>();
  @Output() public conversationTitleEditRequested: EventEmitter<void> = new EventEmitter<void>();
  @Output() public userTyping: EventEmitter<void> = new EventEmitter<void>();
  @Output() public backBtnClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() public blockUser: EventEmitter<void> = new EventEmitter<void>();
  @Output() public scrolledToTop: EventEmitter<void> = new EventEmitter<void>();
  @Output() public dirtyForm: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('chatContent', { static: false }) public chatContent: ElementRef<HTMLElement>;

  public messageForEdit: ChatMessage | null = null;
  public placeholderPhrases: DialogMessagePlaceholders = {
    inputPlaceholder: 'CHAT_DIALOG_TEXTAREA_PLACEHOLDER',
    maxLengthErrorHint: 'CHAT_DIALOG_LARGE_MESSAGE_TEXT_ERROR',
    emptyErrorHint: 'CHAT_DIALOG_EMPTY_ERROR_HINT',
    buttonHint: 'CHAT_MESSAGE_FORM_SEND_BUTTON_HINT',
    typingHint: 'CHAT_USER_IS_TYPING_HINT',
  };
  public readonly NAVIGATE_TO_PROFILE: string = ProjectLinks.LINK_PROFILE_VIEW;

  private debouncer: Subject<void> = new Subject<void>();
  private subscriptions$: Subscription[] = [];
  private heightBeforeScrolledUp: number | null = null;

  public get needsDropdownMenu(): boolean {
    return this.permissions.canBlockChat;
  }

  private chatEngineActiveChannelService = inject(ChatEngineActiveChannelService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.subscribeForScrollEvent();
    this.subscribeMessageAddedEvent();
    this.subscribeToMessagesLoading();
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['activeConversation'] && simpleChanges['activeConversation'].currentValue) {
      this.messageForEdit = null;
      safelyDetectChanges(this.changeDetectorRef);
    }
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onEditMessageStarted(message: ChatMessage): void {
    this.messageForEdit = { ...message };
  }

  public onRemoveMessage(message: ChatMessage): void {
    this.messageRemoved.emit(message);
    if (this.messageForEdit && this.messageForEdit.entity_id === message.entity_id) {
      this.messageForEdit = null;
    }
  }

  public onBackBtnClick(): void {
    this.backBtnClick.emit();
  }

  public onScrolledUp(): void {
    if (this.chatContent) {
      // Save the scroll height before requesting another page
      this.heightBeforeScrolledUp = this.chatContent.nativeElement.scrollHeight;
    }
    this.debouncer.next();
  }

  public onBlockUser(): void {
    this.blockUser.emit();
  }

  public onUserTyping(): void {
    this.userTyping.emit();
  }

  public onSendTextMessage(message: string): void {
    this.sendTextMessage.emit(message);
  }

  public onEditMessageFinished(editMessageBody: EditMessageBody): void {
    this.messageEdited.emit(editMessageBody);
    this.messageForEdit = null;
  }

  public onEnteringMessageAborted(): void {
    this.messageForEdit = null;
  }

  public onEditConversationTitle(): void {
    this.conversationTitleEditRequested.emit();
  }

  public setFormStatus(dirty: boolean): void {
    this.dirtyForm.emit(dirty);
  }

  private subscribeForScrollEvent(): void {
    this.debouncer.pipe(debounceTime(AppValues.DEBOUNCE_TIME)).subscribe(() => this.scrolledToTop.emit());
  }

  private subscribeMessageAddedEvent(): void {
    this.subscriptions$.push(
      this.chatEngineActiveChannelService.messageAdded
        .pipe(delay(AppValues.DEBOUNCE_TIME))
        .subscribe(() => this.scrollToBottom()),
    );
  }

  private subscribeToMessagesLoading(): void {
    this.subscriptions$.push(
      this.chatEngineActiveChannelService.initialMessagesLoaded
        .pipe(delay(AppValues.DEBOUNCE_TIME))
        .subscribe(() => this.scrollToBottom()),
    );
    this.subscriptions$.push(
      this.chatEngineActiveChannelService.moreMessagesLoaded
        .pipe(delay(0))
        .subscribe(() => this.scrollToPreScrolledUpPosition()),
    );
  }

  private scrollToBottom(): void {
    if (this.chatContent) {
      this.chatContent.nativeElement.scrollTop = this.chatContent.nativeElement.scrollHeight;
    }
  }

  private scrollToPreScrolledUpPosition(): void {
    if (this.chatContent && this.heightBeforeScrolledUp) {
      // Did height really change?
      const currentScrollHeight: number = this.chatContent.nativeElement.scrollHeight;
      if (this.heightBeforeScrolledUp < currentScrollHeight) {
        // We've just loaded another page, so current scroll position is 1 whole page upper than expected (L2C-2859).
        // Scroll to a point where user would see 1 more message compared to pre-scroll layout
        const heightPerMessage: number = currentScrollHeight / this.messages.length;
        this.chatContent.nativeElement.scrollTop = currentScrollHeight - this.heightBeforeScrolledUp - heightPerMessage;
      }
    }
  }
}
